import { RxStatsDto } from "../../../../api-client";
import { useEffect, useState, useMemo } from "react";
import useStore from "../../../../helpers/useStore";
import { BIContext } from "../../../../stores/BIDashboard/biDashboard.provider";
import { calculateTotalRxCount } from "../../helpers/rxStatsHelpers";
import { useDebounce } from "../../../Transactions/Insurance/Base/hooks/useDebounce";

interface TotalRxCountData {
  locationId: string;
  locationName: string;
  date: Date;
  totalCount: number;
}

const useRxBlankoCounts = (binType: "day" | "week" | "month" | "year", startDate: Date, endDate: Date, locationIds?: string[]) => {
  const BIStore = useStore(BIContext);
  const { getRxBlankoStats } = BIStore;

  // Debounce date changes
  const debounceStartDate = useDebounce(startDate, 300);
  const debounceEndDate = useDebounce(endDate, 300);

  // Memoize locationIds to avoid unnecessary re-renders
  const memoizedLocationIds = useMemo(() => {
    if (!locationIds || locationIds.length === 0) {
      return undefined;
    }
    return locationIds;
  }, [locationIds?.join(','), locationIds?.length]);

  // Store all fetched stats (for "all locations")
  const [allRxBlankoStats, setAllRxBlankoStats] = useState<RxStatsDto[]>([]);
  // Store filtered stats per selected locations
  const [rxBlankoStats, setRxBlankoStats] = useState<RxStatsDto[]>([]);
  const [totalRxBlankoCountResults, setTotalRxBlankoCountResults] = useState<TotalRxCountData[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);

        let response: RxStatsDto[] | undefined;

        if (debounceStartDate && debounceEndDate) {
          // console.log(`[Rx Blanko Count] Fetching data for date range: ${debounceStartDate} - ${debounceEndDate}`);
          // Always fetch all locations data, don't pass location filter to backend
          response = await getRxBlankoStats(debounceStartDate, debounceEndDate);
          // console.log("[Rx Blanko Count] Backend responded with:", response);
        } else {
          // console.log("[Rx Blanko Count] Fetching all data");
        }

        if (response && response.length > 0) {
          setAllRxBlankoStats(response);
          
          // Apply location filter immediately when setting data
          if (memoizedLocationIds && memoizedLocationIds.length > 0) {
            setRxBlankoStats(response.filter(p => memoizedLocationIds.includes(p.locationId)));
          } else {
            setRxBlankoStats(response);
          }
        } else {
          // console.log("[Rx Blanko Count] No data found for the specified range.");
          setAllRxBlankoStats([]);
          setRxBlankoStats([]);
        }
      } catch (error) {
        // console.log("[Rx Blanko Count] Error fetching Blanko Rx counts:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [debounceStartDate, debounceEndDate, getRxBlankoStats]);

  // Filter locally whenever locations change (but not when data is initially loaded)
  useEffect(() => {
    if (!isLoading && allRxBlankoStats.length > 0) {
      if (memoizedLocationIds && memoizedLocationIds.length > 0) {
        setRxBlankoStats(allRxBlankoStats.filter(p => memoizedLocationIds.includes(p.locationId)));
      } else {
        // No location selected → show all
        setRxBlankoStats(allRxBlankoStats);
      }
    }
  }, [memoizedLocationIds, allRxBlankoStats, isLoading]);

  useEffect(() => {
    if (rxBlankoStats.length > 0) {
      const totalRxBlankoCounts = calculateTotalRxCount(rxBlankoStats, binType);
      setTotalRxBlankoCountResults(totalRxBlankoCounts);
    } else {
      setTotalRxBlankoCountResults([]);
    }
  }, [binType, rxBlankoStats]);

  const availableLocations = useMemo(() => {
    const locMap = new Map<string, string>();
    allRxBlankoStats.forEach((p) => {
      if (p.locationId) locMap.set(p.locationId, p.locationName || p.locationId);
    });
    return Array.from(locMap.entries()).map(([id, name]) => ({ id, name }));
  }, [allRxBlankoStats]);

  return { totalRxBlankoCountResults, isLoading, availableLocations };
};

export default useRxBlankoCounts;